import { faker } from "@faker-js/faker/locale/fr"
import { SourceName } from "../../../types"
import {
  type ApiFinancialDataHealthMissions,
  HealthMissionCategoryName,
  HealthMissionMissionName,
  type HealthMissionsGoals,
  HealthMissionsGoalsEnum,
} from "../../healthMissions"

const float = () => faker.number.float({ min: 0, max: 999_999, fractionDigits: 2 })
const fakeDate = () => faker.date.recent({ days: 365 }).toISOString().split("T")[0]
const maybe = <T>(val: T) => faker.helpers.maybe(() => val, { probability: 0.8 })
const fakeEvolution = () =>
  maybe(faker.number.float({ min: -200, max: 200, fractionDigits: 2 })) ?? null
const keyFigure = () => ({
  mainStat: maybe(float()) ?? null,
  evolution: fakeEvolution(),
})
const missionCountKeyFigure = () => ({
  mainStat: maybe(faker.number.int({ min: 0, max: 150 })) ?? null,
  evolution: fakeEvolution(),
})

const missionsByCategory: Record<
  HealthMissionCategoryName,
  HealthMissionMissionName[]
> = {
  [HealthMissionCategoryName.VACCINATION]: [
    HealthMissionMissionName.FLU,
    HealthMissionMissionName.COVID,
    HealthMissionMissionName.VACCINATION_SCHEDULE,
  ],
  [HealthMissionCategoryName.SCREENING]: [
    HealthMissionMissionName.COLORECTAL_CANCER_SCREENING,
    HealthMissionMissionName.CYSTITIS_TROD,
    HealthMissionMissionName.SORE_THROAT_TROD,
    HealthMissionMissionName.FLU_TROD,
  ],
  [HealthMissionCategoryName.SUPPORT]: [
    HealthMissionMissionName.SHARED_MEDICATION_REVIEW,
    HealthMissionMissionName.PREVENTION_ASSESSMENT,
    HealthMissionMissionName.PREGNANT_WOMEN_CONSULTATIONS,
    HealthMissionMissionName.CHRONIC_DISEASE_CONSULTATIONS,
    HealthMissionMissionName.AVK_AOD_ASTHMA_CONSULTATIONS,
    HealthMissionMissionName.CANCER_CONSULTATIONS,
  ],
}

const fakeGoals = (): HealthMissionsGoals => {
  const firstQuartile = faker.number.int({ min: 10, max: 60 })
  return {
    [HealthMissionsGoalsEnum.FIRST_QUARTILE]: firstQuartile,
    [HealthMissionsGoalsEnum.EXPLOITED_POTENTIAL_75]: faker.number.int({
      min: firstQuartile + 10,
      max: 120,
    }),
  }
}

const fakeActe = () => ({
  name: faker.string.alphanumeric({ length: 3, casing: "upper" }),
  sales: maybe(keyFigure()),
  units: maybe(keyFigure()),
})

const fakeMission = (name: HealthMissionMissionName) => ({
  name,
  acts: Array.from({ length: faker.number.int({ min: 0, max: 3 }) }, fakeActe),
  sales: maybe(keyFigure()),
  units: maybe(missionCountKeyFigure()),
  goals: maybe(fakeGoals()),
})

const fakeCategory = (name: HealthMissionCategoryName) => ({
  name,
  missions: missionsByCategory[name].map(fakeMission),
  sales: maybe(keyFigure()),
  units: maybe(keyFigure()),
})

export const healthMissionsMock = (): ApiFinancialDataHealthMissions => ({
  pharmacyId: faker.number.int({ min: 0, max: 9_999_999_999 }),
  source: SourceName.SANTESTAT,
  periodStartDate: fakeDate(),
  periodEndDate: fakeDate(),
  categories: (
    Object.keys(HealthMissionCategoryName) as HealthMissionCategoryName[]
  ).map(fakeCategory),
})
